<template>
	<v-dialog
		v-model="dialog"
		max-width="900px">
        <v-card class="background-2 round-card">
			<v-card-title class="pa-3 background-1">
				<v-icon class="pl-2">mdi-whatsapp</v-icon>
				<p class="pl-2 pl-xl-3">{{ title }}</p>
				<v-spacer></v-spacer>
				<v-btn
					icon
					@click="downloadMedia"
					v-if="canDownload"
					:loading="loading"
					:disabled="loading">
					<v-icon>mdi-download</v-icon>
				</v-btn>
				<v-tooltip bottom>
					<template v-slot:activator="{ on, attrs }">
						<v-btn
							v-bind="attrs"
							v-on="on"
							icon
							@click="closeDialog">
							<v-icon>mdi-close</v-icon>
						</v-btn>
					</template>
					<span>Download</span>
				</v-tooltip>
			</v-card-title>

			<v-divider></v-divider>

			<v-card-text class="pa-6">
				<div class="media-container background-1">
					<!-- Immagini -->
					<v-img
						v-if="isImage"
						:src="mediaSrc"
						:alt="title"
						contain
						max-height="600"
					></v-img>

					<!-- Video -->
					<video
						v-else-if="isVideo"
						:src="mediaSrc"
						controls
						class="video-player"
					></video>

					<!-- Audio -->
					<div v-else-if="isAudio" class="audio-container pa-4">
						<v-icon size="64" color="primary">mdi-music</v-icon>
						<audio
							:src="mediaSrc"
							controls
							class="audio-player mt-4"
						></audio>
					</div>

					<!-- Documenti -->
					<div v-else-if="isDocument" class="document-container pa-4">
						<v-icon size="64" color="primary">{{ documentIcon }}</v-icon>
						<p class="mt-4 mb-0">{{ fileName }}</p>
						<p class="caption">{{ fileSize }}</p>
						<v-btn
							color="primary"
							@click="downloadMedia"
							class="mt-4"
							:loading="loading"
							:disabled="loading">
							<v-icon left>mdi-download</v-icon>
							Scarica Documento
						</v-btn>
					</div>

					<!-- Altri tipi di file -->
					<div v-else class="unknown-container pa-4 text-center">
						<v-icon size="64" color="grey">mdi-file</v-icon>
						<p class="mt-4">Tipo di file non supportato per la visualizzazione</p>
						<v-btn
							color="primary"
							@click="downloadMedia"
							class="mt-4"
							:loading="loading"
							:disabled="loading">
							<v-icon left>mdi-download</v-icon>
							Scarica File
						</v-btn>
					</div>
				</div>
			</v-card-text>

			<v-card-text v-if="caption" class="pa-4">
				<p class="mb-0">{{ caption }}</p>
			</v-card-text>

			<v-divider></v-divider>

			<v-card-actions class="px-4 pb-2 background-1">
				<v-spacer></v-spacer>
				<v-btn
					text
					@click="closeDialog">
					Chiudi
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
export default {
	name: 'MediaViewer',

	props: {
		value: {
			type: Boolean,
			default: false
		},
		src: {
			type: String,
			default: ''
		},
		type: {
			type: String,
			default: ''
		},
		message: {
			type: Object,
			default: null
		},
		mediaIndex: {
			type: Number,
			default: null
		},
		caption: {
			type: String,
			default: ''
		},
		fileName: {
			type: String,
			default: ''
		},
		fileSize: {
			type: String,
			default: ''
		}
	},

	data() {
		return {
			loading: false
		}
	},

	computed: {
		dialog: {
			get() {
				return this.value
			},
			set(val) {
				this.$emit('input', val)
			}
		},

		mediaSrc() {
			return this.src
		},

		mediaType() {
			if (this.type) return this.type

			if (!this.src) return ''

			const ext = this.src.split('.').pop().toLowerCase()

			if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)) {
				return 'image'
			} else if (['mp4', 'avi', 'mov', 'wmv', 'webm'].includes(ext)) {
				return 'video'
			} else if (['mp3', 'wav', 'ogg', 'm4a'].includes(ext)) {
				return 'audio'
			} else if (['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext)) {
				return 'document'
			}

			return 'unknown'
		},

		isImage() {
			return this.mediaType === 'image'
		},

		isVideo() {
			return this.mediaType === 'video'
		},

		isAudio() {
			return this.mediaType === 'audio'
		},

		isDocument() {
			return this.mediaType === 'document'
		},

		documentIcon() {
			const ext = this.src?.split('.').pop().toLowerCase()

			const icons = {
				'pdf': 'mdi-file-pdf-box',
				'doc': 'mdi-file-word',
				'docx': 'mdi-file-word',
				'xls': 'mdi-file-excel',
				'xlsx': 'mdi-file-excel',
				'ppt': 'mdi-file-powerpoint',
				'pptx': 'mdi-file-powerpoint',
				'txt': 'mdi-file-document',
				'csv': 'mdi-file-delimited'
			}

			return icons[ext] || 'mdi-file-document'
		},

		title() {
			if (this.fileName) return this.fileName

			if (this.isImage) return 'Immagine'
			if (this.isVideo) return 'Video'
			if (this.isAudio) return 'Audio'
			if (this.isDocument) return 'Documento'

			return 'File'
		},

		canDownload() {
			return !!this.src
		}
	},

	methods: {
		closeDialog() {
			this.dialog = false
		},

		async downloadMedia() {
			if (!this.src) return

			this.loading = true

			try {
				// Scarica il file come blob
				let response = await fetch(this.src)

				// Se il download da Twilio fallisce (404 dopo 13 mesi), prova con fallback locale
				if (!response.ok && this.message && this.message.id && this.mediaIndex !== undefined) {
					console.log('Twilio media expired, trying local fallback')
					const token = localStorage.getItem('user-token')
					const baseUrl = this.$store.getters.baseUrl
					const fallbackUrl = `${baseUrl}/api/whatsapp/media/${this.message.id}/${this.mediaIndex}?token=${token}`

					response = await fetch(fallbackUrl)
				}

				if (!response.ok) {
					throw new Error('Download fallito')
				}

				const blob = await response.blob()

				// Determina il nome del file e l'estensione dal Content-Type
				const contentType = response.headers.get('content-type')
				let extension = 'bin'

				if (contentType) {
					if (contentType.includes('image/jpeg')) extension = 'jpg'
					else if (contentType.includes('image/png')) extension = 'png'
					else if (contentType.includes('image/webp')) extension = 'webp'
					else if (contentType.includes('image/gif')) extension = 'gif'
					else if (contentType.includes('video/mp4')) extension = 'mp4'
					else if (contentType.includes('audio/mpeg')) extension = 'mp3'
					else if (contentType.includes('application/pdf')) extension = 'pdf'
				}

				const fileName = this.fileName || `whatsapp-media-${Date.now()}.${extension}`

				// Crea un URL temporaneo per il blob
				const blobUrl = window.URL.createObjectURL(blob)

				// Crea un link e triggera il download
				const link = document.createElement('a')
				link.href = blobUrl
				link.download = fileName
				document.body.appendChild(link)
				link.click()

				// Cleanup
				document.body.removeChild(link)
				window.URL.revokeObjectURL(blobUrl)

			} catch (error) {
				console.error('Errore download:', error)
				this.$store.commit('setSnackbarError')
			} finally {
				this.loading = false
			}
		}
	}
}
</script>

<style scoped>
	.media-container {
		min-height: 200px;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.video-player {
		width: 100%;
		max-height: 600px;
	}

	.audio-container,
	.document-container,
	.unknown-container {
		text-align: center;
		width: 100%;
	}

	.audio-player {
		width: 100%;
		max-width: 500px;
	}
</style>